import { atom, useAtomValue, useSetAtom } from 'jotai';
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useScreenSize } from '@banhala/ui-kit';
import { isNotNil } from 'shared/src/utils';
import { type View } from 'react-native';
import { useAnimatedReaction, useSharedValue } from 'react-native-reanimated';
import { scheduleOnRN } from 'react-native-worklets';
import { useInstanceId, useSafeTimeout } from 'shared/src/hooks';
import { ScrollContext } from '../context/ScrollContext';

const THROTTLE_MS = 100;
const MIN_VIEW_TIME_MS = 500;

const sectionVisibilityAtom = atom<Record<string, Record<string, boolean>>>({});

export const useIsSectionVisible = (key: string) => {
  const instanceId = useInstanceId();
  const derivedAtom = useMemo(
    () => atom(get => isNotNil(get(sectionVisibilityAtom)[instanceId]?.[key])),
    [instanceId, key],
  );
  return useAtomValue(derivedAtom);
};

export const useMarkSectionVisible = () => {
  const instanceId = useInstanceId();
  const set = useSetAtom(sectionVisibilityAtom);
  return useCallback(
    (key: string) => {
      set(prev => {
        if (prev[instanceId]?.[key]) return prev;
        return {
          ...prev,
          [instanceId]: { ...prev[instanceId], [key]: true },
        };
      });
    },
    [instanceId, set],
  );
};

/**
 * 컴포넌트가 뷰포트에 진입했는지 감지하는 훅.
 * 중첩된 서브 섹션 컴포넌트에서 사용.
 *
 * scrollY SharedValue 변화에 반응하여 100ms throttle로 가시 여부를 체크.
 * 연속 MIN_VIEW_TIME_MS 동안 가시 상태였으면 mark — "잠깐 스쳐 지나간"
 * 케이스에서 가짜 DISPLAY가 발화되는 것을 방지. 중간에 viewport를 벗어나면
 * visibleStart는 리셋되어 처음부터 다시 측정한다.
 *
 * mark 이후에는 worklet이 SharedValue 게이트로 즉시 early return.
 *
 * JS 스레드 interval 누적 지연 위험을 제거하기 위해 UI 스레드의 scroll
 * 이벤트를 트리거로 사용. 스크롤이 멈춰있을 때는 호출되지 않으므로
 * 초기 마운트 시점은 useEffect가 즉시 + MIN_VIEW_TIME_MS 후에 한 번 더
 * 검증하여 스크롤 없이 머문 케이스도 cover.
 */
export const useElementViewportTracking = (
  visibilityKey: string,
  offset = 0,
) => {
  const { height: screenHeight } = useScreenSize();
  const { scrollY } = useContext(ScrollContext);
  const [isMarked, setIsMarked] = useState(false);
  const isCheckingRef = useRef(false);
  const viewRef = useRef<View>(null);
  const markVisible = useMarkSectionVisible();
  const visibleStartRef = useRef<number | null>(null);
  const isMarkedSV = useSharedValue(false);
  const lastCheckSV = useSharedValue(0);
  const setSafeTimeout = useSafeTimeout();

  const checkVisibility = useCallback(() => {
    if (isCheckingRef.current) return;
    isCheckingRef.current = true;
    viewRef.current?.measureInWindow((_x, y, _w, h) => {
      const isInViewport = h > 0 && y + h > 0 && y < screenHeight - offset;
      const now = Date.now();
      if (isInViewport) {
        if (visibleStartRef.current === null) {
          visibleStartRef.current = now;
        } else if (now - visibleStartRef.current >= MIN_VIEW_TIME_MS) {
          setIsMarked(true);
          isMarkedSV.value = true;
          markVisible(visibilityKey);
        }
      } else {
        visibleStartRef.current = null;
      }
      isCheckingRef.current = false;
    });
  }, [screenHeight, offset, markVisible, visibilityKey, isMarkedSV]);

  useAnimatedReaction(
    () => (isMarkedSV.value || !scrollY ? null : scrollY.value),
    (current, previous) => {
      if (current === null || current === previous) return;
      const now = Date.now();
      if (now - lastCheckSV.value < THROTTLE_MS) return;
      lastCheckSV.value = now;
      scheduleOnRN(checkVisibility);
    },
  );

  useEffect(() => {
    if (isMarked) return;
    checkVisibility();
    setSafeTimeout(checkVisibility, MIN_VIEW_TIME_MS);
  }, [checkVisibility, isMarked, setSafeTimeout]);

  return viewRef;
};
